import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { PostJson } from '@awp/helpers/fetchReact';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import Table from '@awp/components/table';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import EntityModal from './EntityModal';
import ConfirmModal from '@awp/components/confirmModal';
import { externalObject } from './helpers';
import { getUtcToLocalByFormat } from '@awp/helpers/utilsReact';
import { maxTotalSize, bytesToMB } from './helpers';
import sumBy from 'lodash.sumby';

async function deleteForecast({ eventId, fileId }) {
  const result = await PostJson('/files/delete', { eventId, fileId })
    .then(async (resp) => {
      if (!resp.ok) {
        throw new Error('Network error');
      }

      return { status: true, data: await resp.json() };
    })
    .catch(() => ({ status: false, data: [] }));

  return result;
}

function EntityTable({ eventId, data, isTransmitted, onUpdate = () => {} }) {
  const currentUser = useSelector(selectCurrentUser);
  const [mode, setMode] = useState('');
  const [item, setItem] = useState();
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setConfirm] = useState(false);

  const hasReadPermission = currentUser.Permissions.includes('READ_AVALANCHE_HAZARD');
  const hasWritePermission = currentUser.Permissions.includes('WRITE_AVALANCHE_HAZARD');

  const actionMenu = (row) => {
    const item = row.row.original;
    return (
      <>
        <div>
          <i
            className="fas fa-eye mx-1 my-1 awp-img-btn"
            title="View"
            onClick={() => {
              setMode('view');
              setItem(item);
              setShowModal(true);
            }}
          ></i>
          {hasWritePermission && !isTransmitted && (
            <>
              <i
                className="fas fa-edit mx-1 my-1 awp-img-btn"
                title="Edit"
                onClick={() => {
                  setMode('edit');
                  setItem(item);
                  setShowModal(true);
                }}
              ></i>
              <i
                className="fas fa-trash mx-1 my-1 awp-img-btn"
                title="Delete"
                onClick={async () => {
                  setItem(item);
                  setConfirm(true);
                }}
              ></i>
            </>
          )}
        </div>
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Source',
        accessor: 'SourceDescription',
        sortType: sortByNumericThenAlpha,
        Cell: ({ value }) => {
          return value;
        },
      },
      {
        Header: 'Description',
        accessor: 'RelevanceDescription',
        sortType: sortByNumericThenAlpha,
        Cell: ({ value }) => {
          return value;
        },
      },
      {
        Header: 'Date Uploaded',
        accessor: 'LastUpdateTimestamp',
        sortType: sortByNumericThenAlpha,
        Cell: ({ value }) => {
          return getUtcToLocalByFormat(value, 'yyyy-MM-dd HH:mm');
        },
      },
      {
        Header: 'Uploaded By',
        accessor: 'LastUpdateUserDisplayName',
        sortType: sortByNumericThenAlpha,
        Cell: ({ value }) => {
          return value;
        },
      },
      {
        Header: 'Actions',
        accessor: 'id',
        disableSortBy: true,
        Cell: actionMenu,
      },
    ],
    [hasWritePermission, isTransmitted],
  );

  if (!hasReadPermission) return null;

  const totalFileSize = sumBy(data, (v) => v.ObjectSizeBytes);

  return (
    <>
      <div style={{ textAlign: 'right' }}>
        Total: {`${bytesToMB(totalFileSize)} MB of ${bytesToMB(maxTotalSize)} MB`}
      </div>
      <Table
        id="third_party_forecast_table"
        columns={columns}
        data={data || []}
        fetchData={() => {}}
        loading={false}
        search={[]}
        filters={[]}
        dataTriggers={[]}
      />
      <EntityModal
        show={showModal}
        eventId={eventId}
        data={item}
        mode={mode}
        onClose={() => {
          setShowModal(false);
          if (mode === 'edit') onUpdate();
        }}
      />
      <ConfirmModal
        isOpen={showConfirm}
        toggleModal={() => {
          setConfirm(false);
        }}
        cfmConfig={{
          title: 'Delete Confirmation',
          message: 'Are you sure to delete the weather forecast?',
          cfmBtnText: 'Confirm',
          cancelBtnText: 'Cancel',
          cfmAction: async () => {
            await deleteForecast({ eventId, fileId: item.EventExternalObjectId });
            toast.success('3rd party forecast successfully deleted.');
            setConfirm(false);
            onUpdate();
          },
        }}
        actionState=""
      ></ConfirmModal>
    </>
  );
}

EntityTable.propTypes = {
  eventId: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(externalObject),
  onUpdate: PropTypes.func,
  isTransmitted: PropTypes.bool,
};

export default EntityTable;
